import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { makeAppointmentFilter } from '../../utils/appointmentSearch';
import { fetchCustomerSignoffs, type CustomerSignoffRow } from '../../api/customerSignoffs';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SignoffsTable({
  title,
  sectionId,
  rows,
  emptyText,
  onRowClick,
  extraColumn,
}: {
  title: string;
  sectionId: string;
  rows: CustomerSignoffRow[];
  emptyText: string;
  onRowClick: (id: number) => void;
  extraColumn?: { label: string; key: string; render: (r: CustomerSignoffRow) => React.ReactNode };
}) {
  const headingId = `signoff-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, (r, key) => {
        switch (key) {
          case 'job_code':
            return r.job_code ?? '';
          case 'business':
            return r.business_name ?? '';
          case 'installer':
            return r.installer_name ?? '';
          case 'client':
            return r.client_name ?? '';
          case 'created':
            return r.created_at ?? '';
          case extraColumn?.key:
            return String(extraColumn?.render(r) ?? '');
          default:
            return '';
        }
      }),
    [rows, sortKey, sortDir, extraColumn]
  );
  const colCount = 5 + (extraColumn ? 1 : 0);
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        {title} <span className="events-section-count">({rows.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Job code" columnKey="job_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Business" columnKey="business" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Installer" columnKey="installer" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Client" columnKey="client" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Created" columnKey="created" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              {extraColumn ? (
                <SortableTh label={extraColumn.label} columnKey={extraColumn.key} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="events-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.id} className="events-table-row-clickable" onClick={() => onRowClick(r.id)}>
                  <td data-label="Job code">
                    {r.job_code ? <span className="job-code">{r.job_code}</span> : '—'}
                  </td>
                  <td data-label="Business">{r.business_name}</td>
                  <td data-label="Installer">{r.installer_name || '—'}</td>
                  <td data-label="Client">{r.client_name || '—'}</td>
                  <td data-label="Created">{formatDateTime(r.created_at)}</td>
                  {extraColumn ? <td data-label={extraColumn.label}>{extraColumn.render(r)}</td> : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const CustomerSignOffsTab: React.FC = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState<CustomerSignoffRow[]>([]);
  const [completed, setCompleted] = useState<CustomerSignoffRow[]>([]);
  const [voided, setVoided] = useState<CustomerSignoffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filterRows = useMemo(
    () =>
      makeAppointmentFilter<CustomerSignoffRow>(searchQuery, (r) => [
        r.job_code,
        r.business_name,
        r.installer_name,
        r.client_name,
        r.created_by_username,
      ]),
    [searchQuery]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchCustomerSignoffs();
      setPending(data.pending);
      setCompleted(data.completed);
      setVoided(data.void);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load sign-offs.');
      setPending([]);
      setCompleted([]);
      setVoided([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const go = (id: number) => navigate(`/installation/sign-off/${id}`);

  return (
    <>
      <div className="page-toolbar events-page-toolbar">
        <input
          type="text"
          className="page-toolbar-search"
          placeholder="Search by job code, business, installer…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search customer sign-offs"
        />
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading sign-offs…</p>
      ) : (
        <>
          <SignoffsTable
            title="Pending Signatures"
            sectionId="pending"
            rows={filterRows(pending)}
            emptyText="No pending signatures."
            onRowClick={go}
          />
          <SignoffsTable
            title="Completed Signatures"
            sectionId="completed"
            rows={filterRows(completed)}
            emptyText="No completed signatures."
            onRowClick={go}
            extraColumn={{ label: 'Completed', key: 'completed', render: (r) => formatDateTime(r.completed_at) }}
          />
          <SignoffsTable
            title="Voided Signatures"
            sectionId="void"
            rows={filterRows(voided)}
            emptyText="No voided signatures."
            onRowClick={go}
            extraColumn={{ label: 'Void reason', key: 'void_reason', render: (r) => r.void_reason || '—' }}
          />
        </>
      )}
    </>
  );
};

export default CustomerSignOffsTab;
